import React, { useRef, useEffect, useContext, useState } from 'react';
import { getFirestore, orderBy, limit, doc, getDoc, query, collection, where, getDocs, updateDoc, deleteDoc } from 'firebase/firestore';
import { getAuth } from "firebase/auth";
import { useLocation, useNavigate } from 'react-router-dom';
import { firestore } from './firebase';
import { RealTimeAgo } from './RealTimeAgo';
import { AppContext } from './AppContext';

const db = getFirestore();
const auth = getAuth();

const NotificationsScreen = ({ onProfilePress, onNavigate, userId }) => {
  const {
    userId2,
    activePopupMedia, likes, notifications,
    setCurrentlyLoadingProfile, setActivePopupMedia, reposts, setNotifications
  } = useContext(AppContext); 

  const location = useLocation(); 
  const navigate = useNavigate();

  const [userProfile, setUserProfile] = useState(null);

  const [isSelecting, setIsSelecting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const notifPostsLimit = 20; // Adjust this if needed
  // Ref to store the current summary doc id (e.g. "summary_3")
  const notifLastLoadedSummaryDocRef = useRef(null);
  const [isLoadingNotifs, setIsLoadingNotifs] = useState(false);
  const [allNotifsLoaded, setAllNotifsLoaded] = useState(false);

  const getCurrentUserId = () => {
    const auth = getAuth();
    const user = auth.currentUser;
    return user ? user.uid : null;
  };

  const consolidateNotifications = (notificationsArray) => {
    const groupedNotifications = {};

    notificationsArray.forEach(notification => {
      let key;
      if (notification.postId && notification.commentId) {
        key = `comment-${notification.postId}-${notification.commentId}`;
      } else if (notification.postId && notification.replyId) {
        key = `reply-${notification.postId}-${notification.replyId}`;
      } else {
        // Use notification.id if available; otherwise, fallback to a random key.
        key = `unique-${notification.id || Math.random()}`;
      }

      if (!groupedNotifications[key]) {
        groupedNotifications[key] = [notification];
      } else {
        groupedNotifications[key].push(notification);
      }
    });

    return Object.values(groupedNotifications).map(group => {
      if (group.length > 1) {
        // Consolidate notifications by using a base notification (prefer one of type 'reply' if available)
        return {
          ...group.find(n => n.type === 'reply') || group[0],
          type: 'consolidatedReplyTag',
          originalNotifications: group
        };
      }
      return group[0];
    });
  };

  useEffect(() => {
    const currentUserId = getCurrentUserId();
    if (!currentUserId) {
      console.error('User ID not found.');
      return;
    }
    const userDocRef = doc(db, 'users', currentUserId);
    updateDoc(userDocRef, { hasUnreadNotifications: false })
      .then(() => console.log('Unread notifications status reset.'))
      .catch((error) => console.error('Failed to reset unread notifications:', error));

    // Initial fetch of notifications summary
    fetchNotifications();
  }, []);

  // Function to fetch notifications from the summary docs subcollection
  const fetchNotifications = async (loadMore = false) => {
    const currentUserId = getCurrentUserId();
    if (!currentUserId) return;
    if (loadMore && allNotifsLoaded) return;
    if (isLoadingNotifs) return;
  
    setIsLoadingNotifs(true);
    const notifSummariesRef = collection(db, 'notificationSummaries', currentUserId, 'summaries');
  
    if (!loadMore) {
      // — Initial load: grab the very latest summary doc —
      const summaryQuery = query(notifSummariesRef, orderBy('__name__', 'desc'), limit(1));
      const summarySnapshot = await getDocs(summaryQuery);
      if (summarySnapshot.empty) {
        setNotifications([]);
        setAllNotifsLoaded(true);
        setIsLoadingNotifs(false);
        return;
      }
  
      const latestDoc = summarySnapshot.docs[0];
      notifLastLoadedSummaryDocRef.current = latestDoc.id;
      const summaryData = latestDoc.data();
      let fetchedNotifs = summaryData.notifications || [];
  
      // Sort descending by timestamp (or createdAt.toMillis())
      fetchedNotifs.sort((a, b) => {
        const aTime = a.timestamp ?? (a.createdAt?.toMillis?.() ?? 0);
        const bTime = b.timestamp ?? (b.createdAt?.toMillis?.() ?? 0);
        return bTime - aTime;
      });
  
      // Consolidate and re-sort
      const consolidated = consolidateNotifications(fetchedNotifs);
      consolidated.sort((a, b) => {
        const aTime = a.timestamp ?? (a.createdAt?.toMillis?.() ?? 0);
        const bTime = b.timestamp ?? (b.createdAt?.toMillis?.() ?? 0);
        return bTime - aTime;
      });
  
      setNotifications(consolidated);
      setAllNotifsLoaded(fetchedNotifs.length < notifPostsLimit);
    } else {
      // — Load more: pull the previous summary doc —
      const currentId = notifLastLoadedSummaryDocRef.current;
      const nextIndex = parseInt(currentId.split('_')[1], 10) - 1;
      if (nextIndex < 1) {
        setAllNotifsLoaded(true);
        setIsLoadingNotifs(false);
        return;
      }
  
      const nextDocId = `summary_${nextIndex}`;
      notifLastLoadedSummaryDocRef.current = nextDocId;
      const summaryDocRef = doc(db, 'notificationSummaries', currentUserId, 'summaries', nextDocId);
      const summaryDocSnap = await getDoc(summaryDocRef);
      if (!summaryDocSnap.exists()) {
        setAllNotifsLoaded(true);
        setIsLoadingNotifs(false);
        return;
      }
  
      let fetchedNotifs = summaryDocSnap.data().notifications || [];
      fetchedNotifs.sort((a, b) => {
        const aTime = a.timestamp ?? (a.createdAt?.toMillis?.() ?? 0);
        const bTime = b.timestamp ?? (b.createdAt?.toMillis?.() ?? 0);
        return bTime - aTime;
      });
  
      setNotifications(prev => {
        const combined = [...prev, ...fetchedNotifs];
  
        const consolidated = consolidateNotifications(combined);
        consolidated.sort((a, b) => {
          const aTime = a.timestamp ?? (a.createdAt?.toMillis?.() ?? 0);
          const bTime = b.timestamp ?? (b.createdAt?.toMillis?.() ?? 0);
          return bTime - aTime;
        });
  
        return consolidated;
      });
  
      setAllNotifsLoaded(fetchedNotifs.length < notifPostsLimit);
    }
  
    setIsLoadingNotifs(false);
  };

  const handlePressProfile = (profile) => {
    const clonedProfile = JSON.parse(JSON.stringify(profile));
    const targetRoute = profile.userId === userId2 ? '/mydat/profile' : `/userprofile/${profile.username}`;
    navigate(targetRoute, { state: { profile: clonedProfile, fromURL: false } });
  };





  

  useEffect(() => {
    if (isSelecting && activePopupMedia) {
      try {
        onProfilePress(); // Call the function after activePopupMedia has been updated

        setIsSelecting(false);
      } catch (error) {
        console.error("Error in useEffect for activePopupMedia:", error);
      }
    }
  }, [activePopupMedia]);

  const handleProfileSelect = async (profileData) => {
    try {
      setIsSelecting(true);
      const popupData = preparePopupData(profileData);
      handlePressProfile(popupData); // This triggers the useEffect
    } catch (error) {
      console.error('Error in handleProfileSelect:', error);
    }
  };

  const sortReleasesByTimestamp = (releases) => {
    try {
      const sortedReleases = [...releases].sort((a, b) => b.releaseTimestamp - a.releaseTimestamp);
      return sortedReleases;
    } catch (error) {
      console.error('Error in sortReleasesByTimestamp:', error);
      return [];
    }
  };

  const preparePopupData = (userData) => {
    try {
      
      const albums = Array.isArray(userData?.albums) ? sortReleasesByTimestamp(userData.albums) : [];
      const singles = Array.isArray(userData?.singles) ? sortReleasesByTimestamp(userData.singles) : [];
      const videos = Array.isArray(userData?.videos) ? sortReleasesByTimestamp(userData.videos) : [];

      return {
        artistName: userData?.artistName || userData?.ArtistName || '',
        popupProfileImage: typeof userData?.profileImageUrl === 'string' ? userData.profileImageUrl : '',
        username: typeof userData?.username === 'string' ? userData.username : '',
        userId: typeof userData?.userId === 'string' ? userData.userId : '',
        accountType: typeof userData?.accountType === 'string' ? userData.accountType : '',
        otherReleases: { albums, singles, videos },
        vocalDrop: typeof userData?.vocalDrop === 'string' ? userData?.vocalDrop : false,
        voiceDropMuted: typeof userData?.voiceDropMuted === 'boolean' ? userData?.voiceDropMuted : false,
        bio: typeof userData?.bio === 'string' ? userData.bio : '',
        blurb: typeof userData?.blurb === 'string' ? userData.blurb : '',
        location: typeof userData?.location === 'string' ? userData.location : '',
        timestamp: new Date().getTime(),
        ScreenSKN: Array.isArray(userData?.ScreenSKN) ? userData.ScreenSKN : [],
      };
    } catch (error) {
      console.error('Error in preparePopupData:', error);
      return null;
    }
  };

  const getCachedUserDoc = async (userId) => {
    try {
      const userDocRef = doc(firestore, 'users', userId);
      const userDocSnap = await getDoc(userDocRef);

      if (userDocSnap.exists()) {
        let userData = { ...userDocSnap.data(), userId: userId };
        
        return userData;
      } else {
        console.error(`No document found in Firestore for userId: ${userId}`);
        return null;
      }
    } catch (error) {
      console.error("Error fetching user document:", error);
      return null;
    }
  };

  const fetchUserProfileByUsername = async (username) => {
    if (!username) return;



    try {
      const q = query(collection(firestore, 'users'), where('username', '==', username));
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        const userData = await getCachedUserDoc(doc.id);

        if (userData) {
          handlePressProfile(userData);
        } else {
          console.error(`UserData could not be prepared for username: ${username}`);

        }
      } else {
        console.error(`No user found with username: ${username}`);

      }
    } catch (error) {
      console.error('Error fetching user by username:', error);

    } finally {
      setIsLoading(false);
    }
  };

  const handlePressPost = (post) => {
    // Clone the post object to ensure it only contains cloneable data
    const clonedPost = JSON.parse(JSON.stringify(post));
    navigate('/viewpost', {
      state: {
        post: clonedPost,
        isLiked: !!likes[post.id],
        isReposted: !!reposts[post.id]
      }
    });
  };

  const handlePressNotification = (notification) => {
    try {
      if (
        notification.type === 'newFollower' || 
        notification.type === 'isLive' || 
        notification.type === 'newSingle' || 
        notification.type === 'newAlbum' || 
        notification.type === 'newMusicVideo'
      ) {
        fetchUserProfileByUsername(notification.username); // Assuming the username is part of the notification
      } else if (notification.postId) { // For notifications related to posts
        const postRef = doc(db, 'posts', notification.postId);
        getDoc(postRef).then(documentSnapshot => {
          if (documentSnapshot.exists()) {
            const postData = { id: documentSnapshot.id, ...documentSnapshot.data() };
            const clonedPost = JSON.parse(JSON.stringify(postData));
            navigate('/viewpost', {
              state: {
                post: clonedPost,
                isLiked: notification.isReposted ? !!likes[notification.originalPost] : !!likes[notification.postId],
                isReposted: notification.isReposted
              }
            });
          }
        }).catch(error => {
          console.error('Error fetching post document:', error);
        });
      }
    } catch (error) {
      console.error('Error in handlePressNotification:', error);
    }
  };

  const clearNotificationSummaries = async () => {
    const currentUserId = getCurrentUserId();
    if (!currentUserId) return;
  
    const confirmClear = window.confirm('Are you sure you want to clear all notifications?');
    if (!confirmClear) return;
  
    try {
      const notifSummariesRef = collection(db, 'notificationSummaries', currentUserId, 'summaries');
      const snapshot = await getDocs(notifSummariesRef);
      const deletePromises = snapshot.docs.map(docSnapshot => deleteDoc(docSnapshot.ref));
      await Promise.all(deletePromises);
  
      console.log('Notification summaries cleared.');
      setNotifications([]);
      notifLastLoadedSummaryDocRef.current = null;
      setAllNotifsLoaded(true);
    } catch (error) {
      console.error('Error clearing notification summaries:', error);
    }
  };
  
  

  const getNotificationText = (notification) => {
    try {
      switch (notification.type) {
        case 'like':
          return `${notification.name} liked your post!`;
        case 'comment':
          return `${notification.name} commented on your post: "${notification.text}"`;
        case 'taggedComment':
          return `${notification.name} tagged you in a comment: "${notification.text}"`;
        case 'reply':
          return `${notification.name} replied to your comment: "${notification.text}"`;
        case 'taggedReply':
          return `${notification.name} tagged you in a reply: "${notification.text}"`;
        case 'newFollower':
          return `${notification.name} has started following you!`;
        case 'repost':
          return `${notification.name} has reposted your post!`;
        case 'credit_received':
          return `${notification.fromUserName} sent you "${notification.amount}" credits!`;
        case 'consolidatedReplyTag':
          return `${notification.name} replied and tagged you in a comment: "${notification.text}"`;
        case 'isLive':
          return `${notification.name} is now live!`;
        case 'newSingle':
          return `${notification.name} released a new single!`;
        case 'newAlbum':
          return `${notification.name} released a new album!`;
        case 'newMusicVideo':
          return `${notification.name} released a new music video!`;
        default:
          return `${notification.name} tagged you in a post!`;
      }
    } catch (error) {
      console.error('Error in getNotificationText:', error);
      return 'You have a new notification!';
    }
  };

  return (
<div style={styles.container}>
  {notifications.length > 0 ? (
    <button style={styles.clearButton} onClick={clearNotificationSummaries}>
      Clear Notifications
    </button>
  ) : (
    !isLoadingNotifs && (
      <div style={styles.noNotifsContainer}>
        <p style={styles.noNotifsText}>There’s no notifications available</p>
      </div>
    )
  )}

  <div
    style={styles.scrollContainer}
    onScroll={e => {
      const el = e.target;
      if (
        el.scrollHeight - el.scrollTop - el.clientHeight < 50 &&
        !allNotifsLoaded &&
        !isLoadingNotifs
      ) {
        fetchNotifications(true);
      }
    }}
  >
    {notifications.map((notification, idx) => (
      <div
        key={idx}
        style={styles.notificationItem}
        onClick={() => handlePressNotification(notification)}
      >
        <div style={styles.notificationRow}>
          <p style={styles.notificationText}>{getNotificationText(notification)}</p>
          <RealTimeAgo timestamp={notification.timestamp} style={styles.timeAgoStyle} />
        </div>
      </div>
    ))}
  </div>
</div>

  );
};

const styles = {
  container: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',  // stack children vertically (button then list)
    justifyContent: 'flex-start',
    alignItems: 'center',
    backgroundColor: '#0e061e',
  },
  
  scrollContainer: {
    marginBottom: '250px',
    marginTop: '80px',
    height: '80%', // Adjust this to change the scrollable area height
    width: '90%',
    overflowY: 'scroll',
    padding: '10px',
    borderRadius: '10px',
  },
  notificationItem: {
    padding: '10px',
    borderBottom: '1px solid #ccc',
    cursor: 'pointer',
  },
  notificationText: {
    fontSize: '16px',
    color: 'white'
  },
  postText: {
    fontSize: '14px',
    color: 'grey'
  },
  timeAgoStyle: {
    fontSize: '12px',
    display: 'flex',
    marginTop: '-10px',
    color: 'grey'
  },

  scrollContainer: {
    height: '80vh',
    width: '92%',
    margin: '0 auto',
    marginTop: '0px',
    overflowY: 'scroll',
    paddingBottom: '150px',
  },
  
  clearButton: {
    display: 'block',
    width: '80%',
    margin: '20px auto 10px',
    padding: '14px 0',
    borderRadius: '16px',
    backgroundColor: '#211043',
    color: 'white',
    fontSize: '15px',
    fontWeight: '700',
    letterSpacing: '0.7px',
    border: 'none',
    cursor: 'pointer',
    textAlign: 'center',
  },
  
  
  noNotifsContainer: {
    padding: '10px',
    textAlign: 'center',
    marginTop: '120px',
  },
  
  noNotifsText: {
    color: '#17C3FA',
    fontSize: '14px',
    opacity: 0.65,
  },
  
  notificationItem: {
    width: '90%',
    margin: '8px auto',
    padding: '14px',
    borderRadius: '16px',
    backgroundColor: '#211043',
    cursor: 'pointer',
  },
  
  notificationRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  
  notificationText: {
    color: 'white',
    fontSize: '15px',
    fontWeight: '700',
    letterSpacing: '0.7px',
    flex: 1,
    marginRight: '8px',
  },
  
  timeAgoStyle: {
    color: 'white',
    fontSize: '13px',
    opacity: 0.65,
    textAlign: 'right',
  },
  
};

export default NotificationsScreen;
